import { Unit } from "./units";

interface TypeMeta{
	valueUnit:Unit;
	metricUnit:Unit;
	imperialUnit:Unit;
};

export type UnitSystem=keyof TypeMeta;

function typeTypeMeta<T extends {[type:string]:TypeMeta}>(table:T){
	return <{[key in keyof T]:TypeMeta}>Object.fromEntries(Object.entries(table).map(([k,meta])=>{
		return [<keyof T>k,meta];
	}));
}

/*
	This table must be updated for each MeasurementType added
	valueUnit is the type unit the data is represented internally. This should always be its metric system base unit. For example 'distance', 'smallDistance', 'largeDistance' would all be valueUnit: meters
	metricUnit is the default unit for this type in the metric system
	imperialUnit is the default unit for this type in the imperial system
*/
export const typeMeta=typeTypeMeta({
	angle: {
		valueUnit: 'radians',
		metricUnit: 'degrees',
		imperialUnit: 'degrees',
	},
	azimuth: {
		valueUnit: 'radians',
		metricUnit: 'degrees360',
		imperialUnit: 'degrees360',
	},
	count: {
		valueUnit: 'none',
		metricUnit: 'none',
		imperialUnit: 'none',
	},
	percent: {
		valueUnit: 'none',
		metricUnit: 'percent',
		imperialUnit: 'percent',
	},
	sound: {
		valueUnit: 'deciBel',
		metricUnit: 'deciBel',
		imperialUnit: 'deciBel',
	},
	largeSound: {
		valueUnit: 'deciBel',
		metricUnit: 'megaDeciBel',
		imperialUnit: 'megaDeciBel',
	},
	time: {
		valueUnit: 'second',
		metricUnit: 'second',
		imperialUnit: 'second',
	},
	smallTime: {
		valueUnit: 'second',
		metricUnit: 'millisecond',
		imperialUnit: 'millisecond',
	},
	lonLat: {
		valueUnit: 'lonLatDegree',
		metricUnit: 'lonLatDegree',
		imperialUnit: 'lonLatDegree',
	},
	distance: {
		valueUnit: 'meter',
		metricUnit: 'meter',
		imperialUnit: 'feet',
	},
	elevation: {
		valueUnit: 'meter',
		metricUnit: 'meter',
		imperialUnit: 'feet',
	},
	smallDistance: {
		valueUnit: 'meter',
		metricUnit: 'millimeter',
		imperialUnit: 'inch',
	},
	particleSize: {
		valueUnit: 'meter',
		metricUnit: 'millimeter',
		// smallMetricUnit:'millimeter',
		// mediumMetricUnit:'centimeter',
		// largeMetricUnit:'meter',
		imperialUnit: 'inch',
	},
	area: {
		valueUnit: 'meter2',
		metricUnit: 'meter2',
		imperialUnit: 'yard2',
	},
	largeArea: {
		valueUnit: 'meter2',
		metricUnit: 'kilometer2',
		imperialUnit: 'acre',
	},
	volume: {
		valueUnit: 'meter3',
		metricUnit: 'meter3',
		imperialUnit: 'yard3',
	},
	density: {
		valueUnit: 'kilogramPerMeter3',
		metricUnit: 'kilogramPerMeter3',
		imperialUnit: 'poundsPerYard3',
	},
	smallDensity: {
		valueUnit: 'kilogramPerMeter3',
		metricUnit: 'gramPerCentimeter3',
		imperialUnit: 'gramPerCentimeter3',//???
	},
	largeDensity: {
		valueUnit: 'kilogramPerMeter3',
		metricUnit: 'metrictonPerMeter3',
		imperialUnit: 'shorttonPerYard3',
	},
	pressure: {
		valueUnit: 'bar',
		metricUnit: 'bar',
		imperialUnit: 'poundsPerInch2',
	},
	rockPressure: {
		valueUnit: 'megapascal',//this is wrong should be pascal!!!!
		metricUnit: 'megapascal',
		imperialUnit: 'poundsPerInch2',
	},
	specificEnergy: {
		valueUnit: 'megaJoulePerKilogram',
		metricUnit: 'megaJoulePerKilogram',
		imperialUnit: 'kilocaloriePerPound',
	},
	weight: {
		valueUnit: 'kilogram',
		metricUnit: 'kilogram',
		imperialUnit: 'pounds',
	},
	largeWeight: {
		valueUnit: 'kilogram',
		metricUnit: 'metricton',
		imperialUnit: 'shortton',
	},
	weightPerDistance: {
		valueUnit: 'kilogramPerMeter',
		metricUnit: 'kilogramPerMeter',
		imperialUnit: 'poundsPerFoot',
	},
	velocity: {
		valueUnit: 'meterPerSecond',
		metricUnit: 'meterPerSecond',
		imperialUnit: 'feetPerSecond',
	},
	smallVelocity: {
		valueUnit: 'meterPerSecond',
		metricUnit: 'millimeterPerSecond',
		imperialUnit: 'inchPerSecond',
	},
	largeVelocity: {
		valueUnit: 'meterPerSecond',
		metricUnit: 'kilometerPerHour',
		imperialUnit: 'milePerHour',
	},
	timePerDistance: {
		valueUnit: 'secondPerMeter',
		metricUnit: 'secondPerMeter',
		imperialUnit: 'secondPerFeet',
	},
	smallTimePerDistance: {
		valueUnit: 'secondPerMeter',
		metricUnit: 'millisecondPerMeter',
		imperialUnit: 'millisecondPerFeet',
	},
	vibrationScaledDistance: {
		valueUnit: 'meterPerSquareRootKg',
		metricUnit: 'meterPerSquareRootKg',
		imperialUnit: 'feetPerSquareRootPound',
	},
	airblastScaledDistance: {
		valueUnit: 'meterPerCubeRootKg',
		metricUnit: 'meterPerCubeRootKg',
		imperialUnit: 'feetPerCubeRootPound',
	},
	temperature: {
		valueUnit: 'kelvin',
		metricUnit: 'celsius',
		imperialUnit: 'fahrenheit',
	},
	frequency: {
		valueUnit: 'hertz',
		metricUnit: 'hertz',
		imperialUnit: 'hertz',
	},
	rotationSpeed:{
		valueUnit: 'rotationPerSecond',
		metricUnit: 'rotationPerMinute',
		imperialUnit: 'rotationPerMinute',
	},
	torque:{
		valueUnit: 'newtonMeter',
		metricUnit: 'newtonMeter',
		imperialUnit: 'footPounds',
	},
	force:{
		valueUnit: 'newton',
		metricUnit: 'newton',
		imperialUnit: 'poundForce',
	},
	smallDistancePerPixel: {
		valueUnit: 'meterPerPixel',
		metricUnit: 'milliMeterPerPixel',
		imperialUnit: 'inchPerPixel',
	},
	weightByLargeWeight: {
		valueUnit: 'kilogramPerKilogram',
		metricUnit: 'kilogramPerMetricton',
		imperialUnit: 'poundsPerShortton',
	},
	largeWeightByWeight: {
		valueUnit: 'kilogramPerKilogram',
		metricUnit: 'metricTonPerKilogram',
		imperialUnit: 'shortTonPerPound',
	},
	grade:{
		valueUnit: 'radians',
		metricUnit: 'tanPercent',
		imperialUnit: 'tanPercent',
	},
	gradeRatio: {
		valueUnit: 'radians',
		metricUnit: 'tanRatio',
		imperialUnit: 'tanRatio',
	},
	cohesiveStrength: {
		valueUnit: 'pascal',
		metricUnit: 'pascal',
		imperialUnit: 'poundsPerInch2',
	},
	largeCohesiveStrength: {
		valueUnit: 'pascal',
		metricUnit: 'megapascal',
		imperialUnit: 'poundsPerInch2',
	},
	powderFactorVolume: {
		valueUnit: 'kilogramPerMeter3',
		metricUnit: 'kilogramPerMeter3',
		imperialUnit: 'poundsPerYard3',
	},
	powderFactorWeight: {
		valueUnit: 'kilogramPerKilogram',
		metricUnit: 'kilogramPerMetricton',
		imperialUnit: 'poundsPerShortton',
	},
	powderFactorWeightInverse: {
		valueUnit: 'kilogramPerKilogram',
		metricUnit: 'metricTonPerKilogram',
		imperialUnit: 'shortTonPerPound',
	},
	hour: {
		valueUnit: 'second',
		metricUnit: 'hour',
		imperialUnit: 'hour',
	},
	minute: {
		valueUnit: 'second',
		metricUnit: 'minute',
		imperialUnit: 'minute',
	},
	largeWeightPerDistance: {
		valueUnit: 'metrictonPerMeter',
		metricUnit: 'metrictonPerMeter',
		imperialUnit: 'shorttonPerFeet',
	},
	volumePerDistance: {
		valueUnit: 'meter3PerMeter',
		metricUnit: 'meter3PerMeter',
		imperialUnit: 'yard3PerFeet',
	},
});

export type Type=keyof typeof typeMeta;
export type TypeToUnit={[t in Type]:Unit};
export const types=<Type[]>Object.keys(typeMeta);
